import { toBackendPermissionKey } from "../config/routePermissions";
import { decodeJwtPayload } from "./tenantUserType";

/** True when JWT indicates company or admin (full access in UI until summary loads). */
export function jwtImpliesFullAccess() {
  const token = localStorage.getItem("token");
  if (!token) return false;
  try {
    const u = decodeJwtPayload(token)?.user;
    const t = u?.user_type;
    return t === "company" || t === "admin";
  } catch {
    return false;
  }
}

function permKey(resourceOrKey, action) {
  if (!resourceOrKey) return "";
  if (typeof action === "string" && action.length) {
    return toBackendPermissionKey(resourceOrKey, action);
  }
  const s = String(resourceOrKey);
  if (s.includes(":")) {
    const idx = s.indexOf(":");
    const mod = s.slice(0, idx);
    const act = s.slice(idx + 1);
    return toBackendPermissionKey(mod, act);
  }
  return s;
}

/**
 * Pure permission check from access-summary (same rules as PermissionContext `can`).
 * @param {object|null|undefined} summary
 * @param {string} resource
 * @param {string} action
 */
export function evaluatePermission(summary, resource, action) {
  if (jwtImpliesFullAccess()) return true;
  if (!summary) return false;
  if (summary.fromOwnerBypass) return true;
  const key = permKey(resource, action);
  const perms = summary.permissions || [];
  // Legacy combined reports role only — granular sales/order report perms do not grant dashboard.
  if (key === "dashboard:view" && perms.includes("reports:view")) {
    return true;
  }
  if (key === "sales_reports:view" && perms.includes("reports:view")) return true;
  if (key === "sales_reports:export" && perms.includes("reports:export")) return true;
  if (key === "order_reports:view" && perms.includes("reports:view")) return true;
  if (key === "order_reports:export" && perms.includes("reports:export")) return true;
  {
    const colonIdx = key.indexOf(":");
    if (colonIdx > 0) {
      const mod = key.slice(0, colonIdx);
      const act = key.slice(colonIdx + 1);
      if (
        mod === "catalog" ||
        mod === "products" ||
        mod === "categories" ||
        mod === "inventory"
      ) {
        const legacyMenusKey = `menus:${act}`;
        if (perms.includes(legacyMenusKey)) return true;
      }
    }
  }
  if (key === "settings:create" && perms.includes("settings:update")) return true;
  if (key === "settings:delete" && perms.includes("settings:update")) return true;

  if (key.startsWith("settings:")) {
    const act = key.slice("settings:".length);
    const has = (k) => perms.includes(k);
    if (act === "manage_addon_settings" && has("settings:update")) return true;
  }

  if (key.startsWith("app_management:")) {
    const act = key.slice("app_management:".length);
    const has = (k) => perms.includes(k);
    if (act === "view_apps" && has("app_management:view")) return true;
    if (act === "view_integrations" && has("app_management:view")) return true;
    if (
      act === "add_integrations" &&
      (has("app_management:create") || has("app_management:update"))
    ) {
      return true;
    }
    if (
      act === "disconnect_integration" &&
      (has("app_management:delete") || has("app_management:update"))
    ) {
      return true;
    }
    if (act === "view" && (has("app_management:view_apps") || has("app_management:view_integrations"))) {
      return true;
    }
    if (act === "create" && has("app_management:add_integrations")) return true;
    if (act === "update" && has("app_management:add_integrations")) return true;
    if (act === "delete" && has("app_management:disconnect_integration")) return true;
  }

  return perms.includes(key);
}
